import type { MaterialLibraryEntry } from './types'

export function getConsistencyAssetTypeLabel(type?: string | null) {
  const labels: Record<string, string> = {
    character: '角色',
    scene: '场景',
    prop: '道具',
  }
  return type ? labels[type] || type : '资产'
}

export function parseConsistencyMaterialDescription(description?: string | null) {
  const normalized = String(description || '').trim()
  const matched = normalized.match(/^consistency-(character|scene|prop):(.+)$/)
  if (!matched) {
    return null
  }
  return {
    type: matched[1] as 'character' | 'scene' | 'prop',
    name: matched[2].trim(),
  }
}

export function parseStoryboardVideoDescription(description?: string | null) {
  const normalized = String(description || '').trim()
  const matched = normalized.match(/^storyboard-video:(\d+):(.+)$/)
  if (!matched) {
    return null
  }
  return {
    sceneNumber: Number(matched[1]),
    title: matched[2].trim(),
  }
}

export function sanitizeMaterialName(name?: string | null) {
  const normalized = String(name || '').trim().replace(/\.[^.]+$/, '')
  if (!normalized) {
    return ''
  }
  if (normalized.length > 36 || /cgi-bin|mmwebwx|getmsgimg|wxid|@[0-9]x/i.test(normalized)) {
    return ''
  }
  return normalized.replace(/[_-]+/g, ' ').trim()
}

export function getMaterialTypeLabel(materialType?: string | null) {
  const labels: Record<string, string> = {
    image: '图片素材',
    character_image: '角色参考图',
    background_image: '场景参考图',
    prop_image: '道具参考图',
    video_clip: '视频素材',
    audio_file: '旁白素材',
    music_file: '音乐素材',
    audio: '音频素材',
    music: '音乐素材',
    subtitle: '字幕素材',
  }
  return labels[String(materialType || '').trim()] || '图片素材'
}

export function getMaterialDisplayTitle(entry: MaterialLibraryEntry) {
  const consistency = parseConsistencyMaterialDescription(entry.description)
  if (consistency?.name) {
    return `${getConsistencyAssetTypeLabel(consistency.type)}参考图 · ${consistency.name}`
  }

  const storyboardVideo = parseStoryboardVideoDescription(entry.description)
  if (storyboardVideo) {
    return storyboardVideo.title
      ? `镜头 ${storyboardVideo.sceneNumber} 视频 · ${storyboardVideo.title}`
      : `镜头 ${storyboardVideo.sceneNumber} 视频`
  }

  const readableName = sanitizeMaterialName(entry.name)
  if (readableName) {
    return readableName
  }

  return `${getMaterialTypeLabel(entry.material_type)} · ${entry.project_title}`
}

export function getMaterialDisplayDescription(entry: MaterialLibraryEntry) {
  const consistency = parseConsistencyMaterialDescription(entry.description)
  if (consistency) {
    return `这张图已经作为${getConsistencyAssetTypeLabel(consistency.type)}官方参考归档到素材中心，可直接回带到一致性资产页继续定版。`
  }

  const storyboardVideo = parseStoryboardVideoDescription(entry.description)
  if (storyboardVideo) {
    return `这段视频是第 ${storyboardVideo.sceneNumber} 个镜头的生成结果，已经归档到素材中心，后续可以继续复用、下载或人工替换。`
  }

  if (entry.description) {
    return entry.description
  }

  if (entry.material_type === 'video_clip') {
    return '这段视频当前还没有补充说明。'
  }
  if (entry.material_type === 'audio_file') {
    return '这条旁白素材已经归档到素材中心，可直接回带到终版音频工作台继续混音。'
  }
  if (entry.material_type === 'music_file') {
    return '这条音乐素材已经归档到素材中心，可直接回带到终版音频工作台作为 BGM 继续混音。'
  }
  return '这张图片当前还没有补充说明。'
}

export function getMaterialProjectLabel(entry: MaterialLibraryEntry, linkedProjectId: number | null) {
  return linkedProjectId && entry.project_id === linkedProjectId
    ? '所属项目 · 当前项目'
    : `所属项目 · ${entry.project_title}`
}

export function getMaterialFooterLabel(entry: MaterialLibraryEntry, linkedProjectId: number | null) {
  const consistency = parseConsistencyMaterialDescription(entry.description)
  if (consistency) {
    return `已归档为${getConsistencyAssetTypeLabel(consistency.type)}官方参考`
  }
  const storyboardVideo = parseStoryboardVideoDescription(entry.description)
  if (storyboardVideo) {
    return `已归档为镜头 ${storyboardVideo.sceneNumber} 视频素材`
  }
  if (entry.material_type === 'audio_file') {
    return '已归档为终版旁白素材'
  }
  if (entry.material_type === 'music_file') {
    return '已归档为终版 BGM 素材'
  }
  return linkedProjectId && entry.project_id === linkedProjectId ? '当前项目素材' : `项目 ${entry.project_id}`
}
